import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [actionStatus, setActionStatus] = useState({ type: '', message: '' });
  const [inviteData, setInviteData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'Viewer',
    department: '',
    sendWelcomeEmail: true
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [isExporting, setIsExporting] = useState(false);

  const roles = ['Administrator', 'Manager', 'Accountant', 'Bookkeeper', 'Viewer'];
  const departments = ['Finance', 'Accounting', 'Operations', 'Sales', 'HR'];
  const statusOptions = ['Active', 'Inactive', 'Pending'];

  // TODO: Load users from database
  React.useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        // TODO: Replace with actual API call to load users
        // const data = await userService.getAll();
        // setUsers(data);
        setUsers([]);
      } catch (err) {
        setError(err?.message || 'Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  const validateInviteForm = () => {
    const errors = [];

    if (!inviteData?.email?.trim()) {
      errors?.push('Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(inviteData?.email)) {
      errors?.push('Please enter a valid email address');
    } else if (users?.some(user => user?.email?.toLowerCase() === inviteData?.email?.toLowerCase())) {
      errors?.push('User with this email already exists');
    }

    if (!inviteData?.firstName?.trim()) {
      errors?.push('First name is required');
    }

    if (!inviteData?.lastName?.trim()) {
      errors?.push('Last name is required');
    }

    if (!inviteData?.department?.trim()) {
      errors?.push('Department is required');
    }

    return errors;
  };

  const handleInviteUser = async () => {
    const validationErrors = validateInviteForm();

    if (validationErrors?.length > 0) {
      setActionStatus({
        type: 'error',
        message: validationErrors?.join(', ')
      });
      return;
    }

    setIsLoading(true);
    setActionStatus({ type: '', message: '' });

    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const newUser = {
        id: Math.max(...(users?.map(u => u?.id) || [0]), 0) + 1,
        name: `${inviteData?.firstName} ${inviteData?.lastName}`,
        email: inviteData?.email,
        role: inviteData?.role,
        status: 'Pending',
        lastLogin: null,
        department: inviteData?.department,
        joinDate: new Date()?.toISOString()?.split('T')?.[0],
        avatar: '/assets/images/no_image.png'
      };

      setUsers(prev => [...prev, newUser]);
      setInviteData({
        email: '',
        firstName: '',
        lastName: '',
        role: 'Viewer',
        department: '',
        sendWelcomeEmail: true
      });
      setShowInviteModal(false);

      setActionStatus({
        type: 'success',
        message: `Invitation sent successfully to ${newUser?.email}!`
      });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setActionStatus({ type: '', message: '' });
      }, 3000);

    } catch (error) {
      setActionStatus({
        type: 'error',
        message: 'Failed to send invitation. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    const user = users?.find(u => u?.id === userId);
    if (!user) return;

    const confirmed = window.confirm(
      `Are you sure you want to change ${user?.name}'s status to ${newStatus}?`
    );
    if (!confirmed) return;

    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 500));

      setUsers(prev =>
        prev?.map(user =>
          user?.id === userId ? { ...user, status: newStatus } : user
        )
      );

      setActionStatus({
        type: 'success',
        message: `${user?.name}'s status updated to ${newStatus}`
      });

      setTimeout(() => {
        setActionStatus({ type: '', message: '' });
      }, 3000);

    } catch (error) {
      setActionStatus({
        type: 'error',
        message: 'Failed to update user status'
      });
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    const user = users?.find(u => u?.id === userId);
    if (!user) return;

    const confirmed = window.confirm(
      `Are you sure you want to change ${user?.name}'s role to ${newRole}?`
    );
    if (!confirmed) return;

    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 500));

      setUsers(prev =>
        prev?.map(user =>
          user?.id === userId ? { ...user, role: newRole } : user
        )
      );

      setActionStatus({
        type: 'success',
        message: `${user?.name}'s role updated to ${newRole}`
      });

      setTimeout(() => {
        setActionStatus({ type: '', message: '' });
      }, 3000);

    } catch (error) {
      setActionStatus({
        type: 'error',
        message: 'Failed to update user role'
      });
    }
  };

  const handleDeleteUser = async (userId) => {
    const user = users?.find(u => u?.id === userId);
    if (!user) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete ${user?.name}? This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 500));

      setUsers(prev => prev?.filter(user => user?.id !== userId));

      setActionStatus({
        type: 'success',
        message: `${user?.name} has been removed from the organization`
      });

      setTimeout(() => {
        setActionStatus({ type: '', message: '' });
      }, 3000);

    } catch (error) {
      setActionStatus({
        type: 'error',
        message: 'Failed to delete user'
      });
    }
  };

  const resendInvitation = async (userId) => {
    const user = users?.find(u => u?.id === userId);
    if (!user) return;

    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 500));

      setActionStatus({
        type: 'success',
        message: `Invitation resent to ${user?.email}`
      });

      setTimeout(() => {
        setActionStatus({ type: '', message: '' });
      }, 3000);

    } catch (error) {
      setActionStatus({
        type: 'error',
        message: 'Failed to resend invitation'
      });
    }
  };

  const handleExportUsers = async () => {
    setIsExporting(true);

    try {
      // Simulate export process
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Create CSV content
      const headers = ['Name', 'Email', 'Role', 'Department', 'Status', 'Last Login', 'Join Date'];

      // Move filteredUsers calculation before usage
      const usersToExport = users?.filter(user => {
        const matchesSearch = user?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                             user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                             user?.department?.toLowerCase()?.includes(searchTerm?.toLowerCase());
        const matchesRole = filterRole === 'All' || user?.role === filterRole;
        const matchesStatus = filterStatus === 'All' || user?.status === filterStatus;
        return matchesSearch && matchesRole && matchesStatus;
      });

      const csvContent = [
        headers?.join(','),
        ...usersToExport?.map(user => [
          user?.name,
          user?.email,
          user?.role,
          user?.department,
          user?.status,
          user?.lastLogin || 'Never',
          user?.joinDate
        ]?.join(','))
      ]?.join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL?.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users-export-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      window.URL?.revokeObjectURL(url);

      setActionStatus({
        type: 'success',
        message: 'User data exported successfully!'
      });

      setTimeout(() => {
        setActionStatus({ type: '', message: '' });
      }, 3000);

    } catch (error) {
      setActionStatus({
        type: 'error',
        message: 'Failed to export user data'
      });
    } finally {
      setIsExporting(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterRole('All');
    setFilterStatus('All');
  };

  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         user?.department?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRole = filterRole === 'All' || user?.role === filterRole;
    const matchesStatus = filterStatus === 'All' || user?.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Icon name="Loader2" size={24} className="animate-spin mr-2" />
        <span>Loading users...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center space-x-2">
          <Icon name="AlertCircle" size={16} className="text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Users Management</h2>
          <p className="text-muted-foreground">Manage user accounts, roles, and permissions for your organization</p>
        </div>
        <Button onClick={() => setShowInviteModal(true)} iconName="UserPlus">
          Invite User
        </Button>
      </div>
      {/* Status Messages */}
      {actionStatus?.message && (
        <div className={`p-4 rounded-lg border ${
          actionStatus?.type === 'success' ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400' : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
        }`}>
          <div className="flex items-center space-x-2">
            <Icon
              name={actionStatus?.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
              size={16}
            />
            <span className="text-sm font-medium">{actionStatus?.message}</span>
          </div>
        </div>
      )}
      {/* Filters and Search */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Input
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full"
          />

          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e?.target?.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="All">All Roles</option>
            {roles?.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="All">All Status</option>
            {statusOptions?.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>

          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={handleExportUsers}
              iconName="Download"
              disabled={isExporting}
              className={isExporting ? 'opacity-50' : ''}
            >
              {isExporting ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                  Exporting...
                </>
              ) : (
                'Export Users'
              )}
            </Button>
            {(searchTerm || filterRole !== 'All' || filterStatus !== 'All') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                iconName="X"
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {filteredUsers?.length !== users?.length && (
          <div className="mt-3 text-sm text-muted-foreground">
            Showing {filteredUsers?.length} of {users?.length} users
          </div>
        )}
      </div>
      {/* Users Table */}
      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-foreground">User</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Role</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Department</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Status</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Last Login</th>
                <th className="text-left p-4 text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-muted-foreground">
                    <div className="flex flex-col items-center space-y-2">
                      <Icon name="Users" size={48} className="text-muted-foreground/50" />
                      <p>No users found</p>
                      <p className="text-sm">Start by inviting your first team member</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUsers?.map((user) => (
                  <tr key={user?.id} className="border-t border-border hover:bg-muted/20">
                    <td className="p-4">
                      <div className="flex items-center space-x-3">
                        <img
                          src={user?.avatar}
                          alt={user?.name}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                        <div>
                          <p className="font-medium text-foreground">{user?.name}</p>
                          <p className="text-sm text-muted-foreground">{user?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <select
                        value={user?.role}
                        onChange={(e) => handleRoleChange(user?.id, e?.target?.value)}
                        className="text-sm border-0 bg-transparent text-foreground focus:ring-0"
                      >
                        {roles?.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-foreground">{user?.department}</span>
                    </td>
                    <td className="p-4">
                      <select
                        value={user?.status}
                        onChange={(e) => handleStatusChange(user?.id, e?.target?.value)}
                        className={`text-xs font-medium border-0 bg-transparent focus:ring-0 ${
                          user?.status === 'Active' ? 'text-green-700'
                            : user?.status === 'Inactive' ? 'text-red-700' : 'text-yellow-700'
                        }`}
                      >
                        {statusOptions?.map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-muted-foreground">
                        {user?.lastLogin || 'Never'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        {user?.status === 'Pending' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => resendInvitation(user?.id)}
                            iconName="Mail"
                          >
                            Resend
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Edit"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteUser(user?.id)}
                          iconName="Trash2"
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* User Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-surface border border-border rounded-lg p-6 text-center">
          <Icon name="Users" size={24} className="mx-auto mb-2 text-primary" />
          <h3 className="text-2xl font-semibold text-foreground">{users?.length}</h3>
          <p className="text-sm text-muted-foreground">Total Users</p>
        </div>

        <div className="bg-surface border border-border rounded-lg p-6 text-center">
          <Icon name="CheckCircle" size={24} className="mx-auto mb-2 text-green-500" />
          <h3 className="text-2xl font-semibold text-foreground">
            {users?.filter(u => u?.status === 'Active')?.length}
          </h3>
          <p className="text-sm text-muted-foreground">Active Users</p>
        </div>

        <div className="bg-surface border border-border rounded-lg p-6 text-center">
          <Icon name="Clock" size={24} className="mx-auto mb-2 text-yellow-500" />
          <h3 className="text-2xl font-semibold text-foreground">
            {users?.filter(u => u?.status === 'Pending')?.length}
          </h3>
          <p className="text-sm text-muted-foreground">Pending Invites</p>
        </div>

        <div className="bg-surface border border-border rounded-lg p-6 text-center">
          <Icon name="Shield" size={24} className="mx-auto mb-2 text-primary" />
          <h3 className="text-2xl font-semibold text-foreground">
            {users?.filter(u => u?.role === 'Administrator')?.length}
          </h3>
          <p className="text-sm text-muted-foreground">Administrators</p>
        </div>
      </div>
      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface border border-border rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">Invite New User</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteData({
                    email: '',
                    firstName: '',
                    lastName: '',
                    role: 'Viewer',
                    department: '',
                    sendWelcomeEmail: true
                  });
                  setActionStatus({ type: '', message: '' });
                }}
                disabled={isLoading}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>

            {/* Modal Status Messages */}
            {actionStatus?.message && (
              <div className={`mx-6 mt-4 p-3 rounded-lg border ${
                actionStatus?.type === 'success' ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400' : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
              }`}>
                <div className="flex items-center space-x-2">
                  <Icon
                    name={actionStatus?.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
                    size={16}
                  />
                  <span className="text-sm">{actionStatus?.message}</span>
                </div>
              </div>
            )}

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  value={inviteData?.firstName}
                  onChange={(e) => setInviteData(prev => ({ ...prev, firstName: e?.target?.value }))}
                  placeholder="John"
                  required
                  disabled={isLoading}
                />
                <Input
                  label="Last Name"
                  value={inviteData?.lastName}
                  onChange={(e) => setInviteData(prev => ({ ...prev, lastName: e?.target?.value }))}
                  placeholder="Doe"
                  required
                  disabled={isLoading}
                />
              </div>

              <Input
                label="Email Address"
                type="email"
                value={inviteData?.email}
                onChange={(e) => setInviteData(prev => ({ ...prev, email: e?.target?.value }))}
                placeholder="john.doe@company.com"
                required
                disabled={isLoading}
              />

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Role
                </label>
                <select
                  value={inviteData?.role}
                  onChange={(e) => setInviteData(prev => ({ ...prev, role: e?.target?.value }))}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
                  disabled={isLoading}
                >
                  {roles?.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Department
                </label>
                <select
                  value={inviteData?.department}
                  onChange={(e) => setInviteData(prev => ({ ...prev, department: e?.target?.value }))}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
                  disabled={isLoading}
                >
                  <option value="">Select Department</option>
                  {departments?.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>

              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={inviteData?.sendWelcomeEmail}
                  onChange={(e) => setInviteData(prev => ({ ...prev, sendWelcomeEmail: e?.target?.checked }))}
                  className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
                  disabled={isLoading}
                />
                <span className="text-sm text-foreground">Send welcome email with login instructions</span>
              </label>
            </div>

            <div className="flex justify-end space-x-4 p-6 border-t border-border">
              <Button
                variant="outline"
                onClick={() => {
                  setShowInviteModal(false);
                  setActionStatus({ type: '', message: '' });
                }}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleInviteUser}
                iconName="Send"
                disabled={isLoading}
                className={isLoading ? 'opacity-50' : ''}
              >
                {isLoading ? (
                  <>
                    <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  'Send Invitation'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;
